import { pocApis } from '../../admin/admin.api';

/**
 * Point-of-contact resolution for the job form.
 *
 * `POST /api/analyst/job/create` links contacts by id — `architect_id`, `builder_id`,
 * `owner_id`, `supervisor_id` — and ignores contact details sent inline. The client is not a
 * contact of its own (V36): `client_type` names which of the other three it is.
 * The form still lets the analyst type a contact out, so anything typed has to become a
 * row in the matching `/api/poc/*` master before the job is created; picking an existing
 * contact instead simply reuses its id.
 */

const POC_APIS = {
  architect: pocApis.architects,
  builder: pocApis.builders,
  owner: pocApis.owners,
  supervisor: pocApis.supervisors,
} as const;

export type PocType = keyof typeof POC_APIS;

/** Contact fields shared by the architect/builder/owner masters. */
const CONTACT_FIELDS = ['name', 'phone', 'mobile', 'whatsapp', 'email'] as const;

const trimmed = (value: unknown): string | null => {
  const text = value == null ? '' : String(value).trim();
  return text ? text : null;
};

/**
 * The address column is named `address` on architects and builders but
 * `office_address` on owners.
 */
function addressKey(type: PocType): string {
  return type === 'architect' || type === 'builder' ? 'address' : 'office_address';
}

/** Builds the create payload for one master, or null when nothing was filled in. */
function toCreatePayload(type: PocType, contact: Record<string, any> = {}) {
  if (type === 'supervisor') {
    const name = trimmed(contact.supervisorName);
    const contactNumber = trimmed(contact.supervisorContact);
    if (!name && !contactNumber) return null;
    return { name, contact: contactNumber };
  }

  const payload: Record<string, unknown> = {};
  CONTACT_FIELDS.forEach((field) => {
    payload[field] = trimmed(contact[field]);
  });
  payload[addressKey(type)] = trimmed(contact.officeAddress);

  if (type === 'owner') {
    payload.building_name = trimmed(contact.buildingName);
  }


  const hasAnything = Object.values(payload).some((v) => v !== null);
  return hasAnything ? payload : null;
}

/** Maps a master record back onto the form's contact branch. */
export function toFormContact(type: PocType, record: Record<string, any> = {}) {
  return {
    id: record.id ?? null,
    name: record.name ?? '',
    phone: record.phone ?? '',
    mobile: record.mobile ?? '',
    whatsapp: record.whatsapp ?? '',
    email: record.email ?? '',
    officeAddress: record.address ?? record.officeAddress ?? '',
    ...(type === 'owner' ? { buildingName: record.buildingName ?? '' } : {}),
  };
}

export async function listPocContacts(type: PocType) {
  return POC_APIS[type].list();
}

/**
 * The id to send for one contact: the selected record's, or a freshly created one.
 * Returns null when the analyst left the section empty — those are all optional.
 */
export async function ensurePocId(
  type: PocType,
  contact: Record<string, any> = {},
  existingId?: unknown
): Promise<number | null> {
  if (existingId != null && existingId !== '') return Number(existingId);

  const payload = toCreatePayload(type, contact);
  if (!payload) return null;

  const created: any = await POC_APIS[type].create(payload);
  return created?.id != null ? Number(created.id) : null;
}

/** Resolves every contact on the job form to the ids the create endpoint expects. */
export async function ensureProjectPocIds(values: Record<string, any>) {
  const { architect = {}, builder = {}, owner = {} } = values;

  const [architectId, builderId, ownerId, supervisorId] = await Promise.all([
    ensurePocId('architect', architect, architect.id),
    ensurePocId('builder', builder, builder.id),
    ensurePocId('owner', owner, owner.id),
    ensurePocId('supervisor', owner, owner.supervisorId),
  ]);

  return {
    architect_id: architectId,
    builder_id: builderId,
    owner_id: ownerId,
    supervisor_id: supervisorId,
  };
}
